import EventBus, { showToast, EVENTS, TOAST } from '@mimir/web/eventBus';
import { getPathForScriptWith } from '@mimir/client-common/upload/cefEvalEscaping';
import isEmpty from 'lodash/isEmpty';
import { getGlobalRouter } from '@mimir/web/router';
import { getGlobalStore } from '@mimir/web/store';
import { getItem } from '@mimir/web/util/api';
import mergeRanges from 'merge-ranges';
import type { Segment } from '@mimir/common/util/intervals';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import { exceptionSummary } from '@mimir/common/util/exception';
import { isInElectron, type ApplicationContext } from '@mimir/common/context/applicationContext';
import type { NleOps } from '@mimir/primitive/nle/nleOps';
import type { CepNleOps } from '@mimir/primitive/nle/cep/cepNleOps';

export const getPathForScript = (adobeContext: ApplicationContext, pathForScript: string | null | undefined) => {
  return getPathForScriptWith(adobeContext, pathForScript);
};

// Obtained from https://helpx.adobe.com/premiere-pro/user-guide.html/premiere-pro/using/supported-file-formats.ug.html
const supportedFileExtensions = new Set([
  '3gp',
  '2gp',
  'aac',
  'aiff',
  'aif',
  'asf',
  'asnd',
  'avc',
  'avi',
  'bwf',
  'chproj',
  'crm',
  'dv',
  'gif',
  'heif',
  'hevc',
  'm1v',
  'm2t',
  'm2ts',
  'm2v',
  'm4a',
  'm4v',
  'mkv',
  'mov',
  'mp3',
  'mp4',
  'mpeg',
  'mpe',
  'mpg',
  'mts',
  'mxf',
  'mjpeg',
  'omf',
  'exr',
  'mxr',
  'sxr',
  'r3d',
  'rush',
  'vob',
  'wav',
  'wmv',
  'ai',
  'eps',
  'bmp',
  'dib',
  'rle',
  'dpx',
  'ico',
  'jpe',
  'jpeg',
  'jpg',
  'jfif',
  'png',
  'psd',
  'ptl',
  'prtl',
  'tga',
  'icb',
  'vda',
  'vst',
  'tiff',
  'aaf',
  'aep',
  'aepx',
  'chproj',
  'csv',
  'pbl',
  'txt',
  'tab',
  'edl',
  'prel',
  // 'prproj', NOTE: excluding prproj as project files are handled separately.
  'xml',
  'webp',
  'webm',
  'srt',
  'mogrt'
]);

export const isSupportedFileExtension = (applicationContext: ApplicationContext, inputFileExtension: string) => {
  if (isInElectron(applicationContext) && !applicationContext.dvrMode) {
    return true;
  }

  if (isEmpty(inputFileExtension)) {
    return false;
  }

  let ext = inputFileExtension;
  if (ext.startsWith('.')) {
    ext = ext.substr(1);
  }

  ext = ext.toLowerCase();

  return supportedFileExtensions.has(ext);
};

/**
 * @param filePath Expected to be a local path, or a platform independent file basename.
 */
export const isFileOfType = (filePath: string, extension: string) => {
  if (isEmpty(filePath)) {
    return false;
  }
  return filePath.toLowerCase().endsWith(extension);
};

/**
 * @param filePath Expected to be a local path, or a platform independent file basename.
 */
export const isPremiereProjectFile = (filePath: string) => isFileOfType(filePath, '.prproj');
export const isDrpFile = (filePath: string) => isFileOfType(filePath, '.drp');

export const getSequenceParts = async (nle: CepNleOps, item: Pick<ClientItem, 'id' | 'media'>): Promise<Segment[]> => {
  if (!item.id) {
    throw new Error('Item ID missing');
  }

  const clipTimings = await nle.getUsedPartsInCurrentSequence(item.id);

  if (!clipTimings || !clipTimings.length) {
    return [];
  }

  // add the offset and prepare array of array to merge
  const ranges: Array<[number, number]> = clipTimings.map(clipTiming => {
    // find out if we need to offset for TS segment ranges (partial media available locally)
    let offset = 0;
    if (clipTiming.mimirArtifact?.type === 'TsSegmentRange') {
      const firstSegment = clipTiming.mimirArtifact.segmentTimes.first;
      if (!item.media?.segmentDuration) {
        console.warn(`item.media.segmentDuration not found for item ${item.id} having TS segment range`);
      } else {
        offset = item.media?.segmentDuration.numerator * firstSegment * 1000 / item.media?.segmentDuration.denominator;
      }
    }

    return [
      clipTiming.inPoint + offset,
      clipTiming.outPoint + offset,
    ];
  });

  // merge overlapping segments to make it easier for the UI to render in a flat track
  const merged = mergeRanges(ranges);

  return merged.map((range: [number, number]) => {
    return {
      start: range[0],
      end: range[1]
    };
  });
};

export const syncMimirWithSequenceTime = async (nle: NleOps) => {
  let sequencePositionDetails;
  try {
    sequencePositionDetails = await nle.getCurrentSequencePositionDetails();
  } catch (err: any) {
    showToast(exceptionSummary(err), TOAST.INFO);
    return;
  }

  const { mimirClipDetails } = sequencePositionDetails;
  if (!mimirClipDetails) {
    showToast('No Mimir clip found in the current sequence position', TOAST.INFO);
    return;
  }

  if (mimirClipDetails.mimirArtifact?.type === 'TsSegmentRange') {
    const firstSegment = mimirClipDetails.mimirArtifact.segmentTimes.first;
    const item = await getItem(mimirClipDetails.mimirItemId);
    if (item.media?.segmentDuration) {
      const offset = item.media?.segmentDuration.numerator * firstSegment * 1000 / item.media?.segmentDuration.denominator;
      mimirClipDetails.timeInClip += offset;
    }
  }

  const router = getGlobalRouter();
  const currentRoute = router.currentRoute;
  const currentItem = getGlobalStore().state.timeline.currentItem;
  if (currentRoute.name === 'itemDetails' && currentItem && currentItem.id === mimirClipDetails.mimirItemId) {
    EventBus.emit(EVENTS.PLAYER_JUMP_TO_TIME, mimirClipDetails.timeInClip);
  } else {
    await router.push({
      name: 'itemDetails',
      params: {
        id: mimirClipDetails.mimirItemId,
      },
      query: {
        time: `${mimirClipDetails.timeInClip}`,
      },
    });
  }
};
